/**
 * @file DataTable/index.jsx
 * @description Composant de tableau de données réutilisable
 * 
 * Ce composant gère l'affichage des données sous forme de tableau avec :
 * - Gestion des états (chargement, erreur)
 * - Affichage basique des données
 * - Support de la configuration
 * - Actions CRUD par ligne
 * - Données secondaires extensibles
 * 
 * @example
 * <DataTable
 *   data={data}
 *   loading={loading}
 *   error={error}
 *   columns={columns}
 *   tableConfig={tableConfig}
 * />
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PaginationControls from '@/components/Pagination/PaginationControls';
import { useResponsiveView } from '@/hooks/useResponsiveView';
import DesktopView from './DesktopView';
import ResponsiveView from './ResponsiveView';

const DataTable = ({
  data,
  loading,
  error,
  columns,
  tableConfig,
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  onSearch,
  onFilterChange,
  onSortChange,
  refreshData,
  actions,
  collapsibleColumns
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const { viewMode } = useResponsiveView();

  const toggleRow = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  // Fonction pour formater les dates
  const formatDate = (date) => {
    if (!date) return 'Non disponible';
    if (typeof date === 'string') {
      date = new Date(date);
    }
    if (!(date instanceof Date) || isNaN(date)) return 'Date invalide';
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Affichage de l'état de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Affichage des données
  return (
    <div className="flex flex-col h-[calc(100vh-220px)]">
      <div className="flex-1 overflow-auto">
        {viewMode === 'table' ? (
          <DesktopView
            data={data}
            columns={columns}
            actions={actions}
            expandedRows={expandedRows}
            toggleRow={toggleRow}
            formatDate={formatDate}
            collapsibleColumns={collapsibleColumns}
          />
        ) : (
          <ResponsiveView
            data={data.items}
            columns={columns}
            rowActions={actions?.row}
            collapsibleColumns={collapsibleColumns}
          />
        )}
      </div>
      <PaginationControls
        currentPage={currentPage}
        totalPages={Math.ceil(totalItems / itemsPerPage)}
        onPageChange={onPageChange}
        rowsPerPage={itemsPerPage}
        onRowsPerPageChange={onItemsPerPageChange}
        totalItems={totalItems}
      />
    </div>
  );
};

DataTable.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired
  }).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.object,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func
    })
  ).isRequired,
  tableConfig: PropTypes.object.isRequired,
  currentPage: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onItemsPerPageChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  refreshData: PropTypes.func.isRequired,
  actions: PropTypes.shape({
    row: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        title: PropTypes.string.isRequired,
        className: PropTypes.string
      })
    )
  }),
  collapsibleColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func.isRequired
    })
  )
};

export default DataTable; 